import React, { useState, useEffect, useCallback, useRef } from "react";
import QRCode from "qrcode";
import {
  listEvents,
  getEvent,
  listMyEvents,
  createEvent,
  updateEvent,
  uploadEventCover,
  createTicketOrder,
  createGuestTicketOrder,
  registerFreeTickets,
  payTicketOrderWallet,
  payTicketOrderBank,
  checkTicketOrderBank,
  initializeTicketCardPayment,
  confirmTicketCardPayment,
  collectEventAnalytics,
  listMyTickets,
  checkInTicket,
  getTicketOrder,
  quoteTicketOrder,
  getTicketPassAvailability,
  getGoogleWalletPassUrl,
  downloadAppleWalletPass,
  getMe,
  getToken,
} from "../api/client";
import { openPaystackCheckout } from "../services/paystack";
import { useAppConfig } from "../hooks/useAppConfig";
import { useColors } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import { formatNairaFromKobo, formatValidForCountdown } from "../utils/money";
import EventDateTimePicker, { toLocalDateTimeValue } from "./EventDateTimePicker";
import TicketQrScanner from "./TicketQrScanner";
import ConfirmDialog from "./ConfirmDialog";
import { buildTicketPdfBlob, downloadBlob, shareTicketPdf } from "../utils/ticketPdf";
import { shareEventLink } from "../utils/eventShare";
import EventAnalyticsPanel from "./EventAnalyticsPanel";
import {
  beginPageView,
  currentAttribution,
  interestDedupeKey,
  markOpenedFromDiscovery,
} from "../utils/eventAnalytics";

const SELECTION_KEY = "pp_ticket_selection";

const ticketFeeKobo = (subtotal, bps) => {
  const rate = Number(bps) || 0;
  if (rate <= 0) return 0;
  return Math.round(Number(subtotal || 0) * (rate / 10000));
};

const serviceFeeForSelection = (ticketTypes, qty, fees) => {
  const flat = Number(fees?.ticketFlatFeeKobo) || 0;
  if (flat <= 0) return null;
  const paidQty = (ticketTypes || []).reduce((sum, ticket) => {
    const quantity = Number(qty?.[ticket.id]) || 0;
    return sum + (Number(ticket.priceKobo) > 0 && quantity > 0 ? quantity : 0);
  }, 0);
  return paidQty * flat;
};

const processingIsConfigured = (fees) => {
  const card = fees?.processing?.card || {};
  return (Number(card.bps) || 0) > 0
    || (Number(card.flatKobo) || 0) > 0
    || (Number(fees?.processing?.bankTransferFlatKobo) || 0) > 0
    || (Number(fees?.processing?.walletFlatKobo) || 0) > 0;
};

const emailLooksValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const priceLabel = (kobo) => (Number(kobo) === 0 ? "FREE" : formatNairaFromKobo(kobo));

const selectionKindOf = (event, qty) => {
  const selected = (event?.ticketTypes || []).filter((ticket) => Number(qty?.[ticket.id]) > 0);
  if (!selected.length) return "none";
  const hasFree = selected.some((ticket) => Number(ticket.priceKobo) === 0);
  const hasPaid = selected.some((ticket) => Number(ticket.priceKobo) > 0);
  if (hasFree && hasPaid) return "mixed";
  if (hasFree) return "free";
  return "paid";
};

const freeIdempotencyKey = (eventId, email, items) => {
  const fingerprint = [
    eventId,
    String(email || "").trim().toLowerCase(),
    ...items.map((item) => `${item.ticketTypeId}:${item.quantity}`),
  ].join("|");
  const storageKey = `pp_free_reg:${fingerprint}`;
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return { key: existing, storageKey };
    const raw = (globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`)
      .replace(/[^A-Za-z0-9_.:-]/g, "");
    const key = (raw.length >= 8 ? raw : `${raw}pendpayreg`).slice(0, 80);
    sessionStorage.setItem(storageKey, key);
    return { key, storageKey };
  } catch {
    return { key: `pendpay${Date.now().toString(36)}reg`, storageKey: "" };
  }
};

/** Digits only — strips letters and other non-numeric input. */
const digitsOnly = (raw) => String(raw || "").replace(/\D/g, "");

const EMPTY_TIER = { name: "", description: "", priceNaira: "", quantityTotal: "" };

const emptyForm = () => ({
  title: "",
  location: "",
  startsAt: "",
  endsAt: "",
  endsAtEnabled: false,
  tag: "",
  description: "",
  coverImageUrl: "",
  tiers: [{ ...EMPTY_TIER }],
});

const formatEventWhen = (startsAt) => {
  if (!startsAt) return "";
  const d = new Date(startsAt);
  return d.toLocaleString("en-NG", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatEventWhenShort = (startsAt, location, endsAt) => {
  if (!startsAt) return location || "";
  const d = new Date(startsAt);
  const date = d.toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "short" });
  let when = date;
  if (endsAt) {
    const end = new Date(endsAt);
    const sameDay =
      d.getFullYear() === end.getFullYear() &&
      d.getMonth() === end.getMonth() &&
      d.getDate() === end.getDate();
    if (sameDay) {
      const startTime = d.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });
      const endTime = end.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });
      when = `${date} · ${startTime}–${endTime}`;
    } else {
      const endDate = end.toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "short" });
      when = `${date} – ${endDate}`;
    }
  }
  return location ? `${when} · ${location}` : when;
};

const formatEventWhenDetail = (startsAt, endsAt) => {
  if (!startsAt) return "";
  if (!endsAt) return formatEventWhen(startsAt);
  return `${formatEventWhen(startsAt)} – ${formatEventWhen(endsAt)}`;
};

const WHEN_FILTERS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "thisWeek", label: "This week" },
];

const SORT_OPTIONS = [
  { id: "starts_at_asc", label: "Soonest" },
  { id: "starts_at_desc", label: "Latest" },
  { id: "title_asc", label: "Title A–Z" },
];

function QrImage({ value, size = 200 }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(value, { width: size, margin: 2, color: { dark: "#0B1120", light: "#FFFFFF" } })
      .then((url) => { if (!cancelled) setSrc(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [value, size]);
  if (!src) {
    return (
      <div style={{ width: size, height: size, background: "#fff", borderRadius: 8 }} />
    );
  }
  return <img src={src} alt="Ticket QR" width={size} height={size} style={{ borderRadius: 8 }} />;
}

/**
 * Events / tickets flow for web.
 * @param {{ guest?: boolean, onRequireAuth?: () => void, onCreateAccount?: () => void, onBack?: () => void }} props
 */
export default function EventsScreen({ guest = false, onRequireAuth, onCreateAccount, onBack, pendingEventId, clearPendingEventId }) {
  const C = useColors();
  const showToast = useToast();
  const { config } = useAppConfig();
  const [view, setView] = useState("discover"); // discover | tickets | managing | detail | guestCheckout | pay | success | create | checkin | analytics
  const [returnView, setReturnView] = useState("discover"); // discover | tickets | managing
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [query, setQuery] = useState("");
  const [whenFilter, setWhenFilter] = useState("upcoming");
  const [sortBy, setSortBy] = useState("starts_at_asc");
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [qty, setQty] = useState({});
  const [order, setOrder] = useState(null);
  const [guestToken, setGuestToken] = useState(null);
  const [guestForm, setGuestForm] = useState({ name: "", email: "", phone: "" });
  const [showAccountInvite, setShowAccountInvite] = useState(true);
  const [payMethod, setPayMethod] = useState("wallet");
  const [chargeQuote, setChargeQuote] = useState(null);
  const [funding, setFunding] = useState(null);
  const [fundingNowMs, setFundingNowMs] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [passAvail, setPassAvail] = useState({ apple: false, google: false });
  const [checkToken, setCheckToken] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [showManualCheckIn, setShowManualCheckIn] = useState(false);
  const [checkInEventId, setCheckInEventId] = useState(null);
  const [checkInEventTitle, setCheckInEventTitle] = useState(null);
  const [meId, setMeId] = useState(null);
  const [profileReady, setProfileReady] = useState(() => guest || !getToken());
  const [meDemo, setMeDemo] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const coverInputRef = useRef(null);
  const pollRef = useRef(null);
  const openedPendingRef = useRef(null);

  const isOrganizer = !!(event && meId && String(event.organizer?.id) === String(meId));

  const refreshOpenEvent = useCallback(async (id) => {
    if (!id) return;
    try {
      const data = await getEvent(id);
      setEvent((current) => (current?.id === id ? data.event : current));
    } catch {
      /* Keep the inventory already on screen if a refresh fails. */
    }
  }, []);

  useEffect(() => {
    if (view !== "detail" || !event?.id || !isOrganizer) return undefined;
    const id = event.id;
    const refresh = () => { void refreshOpenEvent(id); };
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    const expiries = (event.ticketTypes || [])
      .map((ticket) => (ticket.holdExpiresAt ? new Date(ticket.holdExpiresAt).getTime() : NaN))
      .filter((time) => Number.isFinite(time) && time > Date.now());
    const wait = expiries.length
      ? Math.min(Math.max(1000, Math.min(...expiries) - Date.now() + 1500), 60 * 60 * 1000)
      : null;
    const timer = wait == null ? null : window.setTimeout(refresh, wait);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) window.clearTimeout(timer);
    };
  }, [view, event?.id, event?.ticketTypes, isOrganizer, refreshOpenEvent]);

  const analyticsClient = getToken() ? "web_signed_in" : "web_public";

  useEffect(() => {
    if (view !== "detail" || !event?.id || !profileReady || isOrganizer) return;
    const dedupeKey = beginPageView(event.id);
    if (!dedupeKey) return;
    const fields = currentAttribution(analyticsClient);
    void collectEventAnalytics(event.id, {
      kind: "page_view",
      dedupeKey,
      visitorId: fields.visitorId,
      ref: fields.ref,
      utmSource: fields.utmSource,
      utmMedium: fields.utmMedium,
      referrer: fields.referrer,
      client: analyticsClient,
    });
  }, [view, event?.id, isOrganizer, analyticsClient, profileReady]);

  useEffect(() => {
    if (view !== "detail" || !event?.id || !profileReady || isOrganizer) return;
    const selected = Object.values(qty).some((amount) => Number(amount) > 0);
    if (!selected) return;
    const interest = interestDedupeKey(event.id);
    if (!interest.fresh) return;
    const fields = currentAttribution(analyticsClient);
    void collectEventAnalytics(event.id, {
      kind: "ticket_interest",
      dedupeKey: interest.key,
      visitorId: fields.visitorId,
      ref: fields.ref,
      utmSource: fields.utmSource,
      utmMedium: fields.utmMedium,
      referrer: fields.referrer,
      client: analyticsClient,
    });
  }, [qty, view, event?.id, isOrganizer, analyticsClient, profileReady]);

  const ownsEvent = (ev) => {
    if (!ev || !meId) return false;
    if (ev.organizer?.id != null) return String(ev.organizer.id) === String(meId);
    // Events from /events/mine are already scoped to the current user
    return myEvents.some((mine) => mine.id === ev.id);
  };

  const loadDiscover = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listEvents({
        q: query.trim() || undefined,
        upcoming: whenFilter === "upcoming" || undefined,
        thisWeek: whenFilter === "thisWeek" || undefined,
        sort: sortBy,
      });
      setEvents(data.events || []);
    } catch (err) {
      showToast(err.message || "Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [query, whenFilter, sortBy, showToast]);

  useEffect(() => {
    if (view === "discover") loadDiscover();
  }, [view, loadDiscover]);

  const stopBankPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (view !== "pay") stopBankPoll();
  }, [view]);

  useEffect(() => {
    if (view !== "pay" || !order?.id || order.status !== "pending") return undefined;
    let cancelled = false;
    quoteTicketOrder(order.id, payMethod, guestToken)
      .then((data) => {
        if (!cancelled) setChargeQuote(data.quote || null);
      })
      .catch(() => {
        if (!cancelled) setChargeQuote(null);
      });
    return () => {
      cancelled = true;
    };
  }, [view, order?.id, order?.status, payMethod, guestToken]);

  useEffect(() => () => {
    stopBankPoll();
  }, []);

  useEffect(() => {
    if (!funding?.expiresAt) return undefined;
    setFundingNowMs(Date.now());
    const timer = setInterval(() => {
      const now = Date.now();
      setFundingNowMs(now);
      if (new Date(funding.expiresAt).getTime() <= now) {
        stopBankPoll();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [funding?.expiresAt]);

  const selectPayMethod = (next) => {
    if (next === payMethod) return;
    if (payMethod === "bank_transfer") stopBankPoll();
    setPayMethod(next);
    setFunding(null);
  };

  const leavePayView = () => {
    stopBankPoll();
    setFunding(null);
    setView("detail");
  };

  useEffect(() => {
    if (guest || !getToken()) {
      setMeId(null);
      setMeDemo(false);
      setProfileReady(true);
      return undefined;
    }
    let cancelled = false;
    setProfileReady(false);
    getMe()
      .then((me) => {
        if (cancelled) return;
        setMeId(me.id || me.user?.id || null);
        setMeDemo(me.is_demo === true || me.isDemo === true);
      })
      .catch(() => {
        if (cancelled) return;
        setMeId(null);
        setMeDemo(false);
      })
      .finally(() => {
        if (!cancelled) setProfileReady(true);
      });
    return () => { cancelled = true; };
  }, [guest]);

  useEffect(() => {
    if (guest || !getToken()) return undefined;
    let raw = null;
    try {
      raw = sessionStorage.getItem(SELECTION_KEY);
      if (raw) sessionStorage.removeItem(SELECTION_KEY);
    } catch {
      return undefined;
    }
    if (!raw) return undefined;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }
    if (!parsed?.eventId) return undefined;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const data = await getEvent(parsed.eventId);
        if (cancelled) return;
        setEvent(data.event);
        const initial = {};
        (data.event.ticketTypes || []).forEach((t) => {
          const wanted = Number(parsed.qty?.[t.id]) || 0;
          const remaining = Math.max(0, Number(t.quantityRemaining) || 0);
          initial[t.id] = Math.min(remaining, Math.max(0, wanted));
        });
        setQty(initial);
        setReturnView("discover");
        setView("detail");
      } catch (err) {
        if (!cancelled) showToast(err.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [guest, showToast]);

  const requireAuth = () => {
    if (guest || !getToken()) {
      onRequireAuth?.();
      return false;
    }
    return true;
  };

  const openEvent = async (id, fromView = returnView) => {
    setBusy(true);
    try {
      const data = await getEvent(id);
      setEvent(data.event);
      const initial = {};
      (data.event.ticketTypes || []).forEach((t) => { initial[t.id] = 0; });
      setQty(initial);
      setReturnView(fromView);
      setView("detail");
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!pendingEventId) {
      openedPendingRef.current = null;
      return undefined;
    }
    if (openedPendingRef.current === pendingEventId) return undefined;
    openedPendingRef.current = pendingEventId;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const data = await getEvent(pendingEventId);
        if (cancelled) return;
        setEvent(data.event);
        const initial = {};
        (data.event.ticketTypes || []).forEach((t) => { initial[t.id] = 0; });
        setQty(initial);
        setReturnView("discover");
        setView("detail");
      } catch (err) {
        if (!cancelled) showToast(err.message);
      } finally {
        if (!cancelled) {
          setBusy(false);
          clearPendingEventId?.();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [pendingEventId, clearPendingEventId, showToast]);

  const subtotalKobo = () => {
    if (!event) return 0;
    return (event.ticketTypes || []).reduce((sum, t) => sum + t.priceKobo * (qty[t.id] || 0), 0);
  };

  const checkoutFeeKobo = () => {
    const flat = serviceFeeForSelection(event?.ticketTypes, qty, config?.fees);
    return flat == null ? ticketFeeKobo(subtotalKobo(), config?.fees?.ticketFeeBps) : flat;
  };

  const checkoutTotalKobo = () => subtotalKobo() + checkoutFeeKobo();

  const selectedItems = () =>
    (event?.ticketTypes || [])
      .filter((t) => (qty[t.id] || 0) > 0)
      .map((t) => ({ ticketTypeId: t.id, quantity: qty[t.id] }));

  const goPay = async () => {
    const items = selectedItems();
    if (!items.length) {
      showToast("Select at least one ticket");
      return;
    }
    const kind = selectionKindOf(event, qty);
    if (kind === "free" || guest || !getToken()) {
      setView("guestCheckout");
      return;
    }
    setBusy(true);
    try {
      const data = await createTicketOrder(event.id, items, currentAttribution(analyticsClient));
      stopBankPoll();
      setGuestToken(null);
      setOrder(data.order);
      setPayMethod("wallet");
      setFunding(null);
      setView("pay");
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  const stashSelectionAndSignIn = () => {
    if (event?.id) {
      try {
        sessionStorage.setItem(SELECTION_KEY, JSON.stringify({ eventId: event.id, qty }));
      } catch {
        /* private mode */
      }
    }
    onRequireAuth?.();
  };

  const submitGuestCheckout = async () => {
    const name = guestForm.name.trim();
    const email = guestForm.email.trim();
    const phone = guestForm.phone.trim();
    if (name.length < 2) {
      showToast("Enter your full name");
      return;
    }
    if (!emailLooksValid(email)) {
      showToast("Enter a valid email address");
      return;
    }
    if (phone && !/^\+?[0-9][0-9\s-]{6,18}$/.test(phone)) {
      showToast("Enter a valid phone number");
      return;
    }
    const items = selectedItems();
    if (!items.length) {
      showToast("Select at least one ticket");
      return;
    }
    setBusy(true);
    try {
      const free = selectionKindOf(event, qty) === "free";
      const attribution = currentAttribution(guest || !getToken() ? "web_public" : "web_signed_in");
      let data;
      if (free) {
        const idem = freeIdempotencyKey(event.id, email, items);
        data = await registerFreeTickets(event.id, {
          buyerName: name,
          buyerEmail: email,
          buyerPhone: phone,
          items,
          idempotencyKey: idem.key,
          attribution,
        });
        if (idem.storageKey) {
          try { sessionStorage.removeItem(idem.storageKey); } catch { /* private mode */ }
        }
      } else {
        data = await createGuestTicketOrder(event.id, {
          buyerName: name,
          buyerEmail: email,
          buyerPhone: phone,
          items,
          attribution,
        });
      }
      const token = data.order?.guestAccessToken || null;
      stopBankPoll();
      setGuestToken(token);
      setOrder(data.order);
      setPayMethod("card");
      setFunding(null);
      setView(free || data.order?.status === "registered" || data.order?.isFreeRegistration ? "success" : "pay");
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startBankPoll = (orderId, token = guestToken) => {
    stopBankPoll();
    pollRef.current = setInterval(async () => {
      try {
        const data = await checkTicketOrderBank(orderId, token);
        if (data.paid) {
          stopBankPoll();
          setOrder(data.order);
          await afterPaid(data.order);
        } else if (data.refundRequired || data.refundPending || data.refundFailed || data.refunded || String(data.order?.status || "").startsWith("refund_")) {
          stopBankPoll();
          setOrder(data.order);
          setView("refund");
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
  };

  const afterPaid = async (paidOrder) => {
    stopBankPoll();
    setOrder(paidOrder);
    setView("success");
    if (paidOrder?.isGuest) {
      setPassAvail({ apple: false, google: false });
      return;
    }
    try {
      const avail = await getTicketPassAvailability(paidOrder.id);
      setPassAvail(avail);
    } catch {
      setPassAvail({ apple: false, google: false });
    }
  };

  const payNow = async () => {
    if (!order) return;
    setBusy(true);
    try {
      if (payMethod === "wallet") {
        if (order.isGuest || guestToken) {
          showToast("Wallet payment is only available when you are signed in");
          return;
        }
        const data = await payTicketOrderWallet(order.id);
        if (data.refundRequired || data.refundPending || data.refundFailed || data.refunded || String(data.order?.status || "").startsWith("refund_")) {
          setOrder(data.order);
          setView("refund");
          return;
        }
        await afterPaid(data.order);
      } else if (payMethod === "bank_transfer") {
        const data = await payTicketOrderBank(order.id, guestToken);
        if (data.alreadyPaid) {
          await afterPaid(data.order);
        } else {
          setFunding(data.fundingInstructions);
          setOrder(data.order);
          startBankPoll(order.id, guestToken);
        }
      } else if (payMethod === "card") {
        const init = await initializeTicketCardPayment(order.id, guestToken);
        const email = order.buyerEmail;
        const me = email ? null : await getMe();
        if (!config?.paystackPublicKey) {
          throw new Error("Card payments are not configured");
        }
        await openPaystackCheckout({
          publicKey: config.paystackPublicKey,
          email: email || me?.email || `${me?.phone || "guest"}@pendpay.app`,
          amountKobo: init.amountKobo,
          reference: init.reference,
          channels: init.channels,
        });
        const confirmed = await confirmTicketCardPayment(order.id, init.reference, guestToken);
        if (confirmed.refundRequired || confirmed.refundPending || confirmed.refundFailed || confirmed.refunded || String(confirmed.order?.status || "").startsWith("refund_")) {
          setOrder(confirmed.order);
          setView("refund");
          return;
        }
        await afterPaid(confirmed.order);
      }
    } catch (err) {
      showToast(err.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  const loadTickets = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const tk = await listMyTickets();
      setMyTickets(tk.tickets || []);
      setReturnView("tickets");
      setView("tickets");
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadManaging = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const ev = await listMyEvents();
      setMyEvents(ev.events || []);
      setReturnView("managing");
      setView("managing");
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  const openCheckIn = (ev = null) => {
    if (!requireAuth()) return;
    setCheckToken("");
    setCheckResult(null);
    setShowManualCheckIn(false);
    if (ev?.id) {
      setCheckInEventId(ev.id);
      setCheckInEventTitle(ev.title || null);
    } else {
      setCheckInEventId(null);
      setCheckInEventTitle(null);
    }
    setView("checkin");
  };

  const soldSummary = (ev) => {
    const types = ev?.ticketTypes || [];
    const summary = ev?.organizerSummary;
    const pricing = summary?.pricing || ev?.pricing;
    if (!types.length && !summary) return null;
    const total = summary?.capacity ?? types.reduce((sum, t) => sum + (Number(t.quantityTotal) || 0), 0);
    if (pricing === "free") {
      const registered = summary?.freeTickets ?? types.reduce((sum, t) => sum + (Number(t.quantitySold) || 0), 0);
      return `Registrations: ${registered} / ${total} · Ticket revenue: ${formatNairaFromKobo(summary?.revenueKobo || 0)}`;
    }
    if (pricing === "mixed") {
      const freeTickets = summary?.freeTickets ?? types.filter((t) => Number(t.priceKobo) === 0).reduce((sum, t) => sum + (Number(t.quantitySold) || 0), 0);
      const paidTickets = summary?.paidTickets ?? types.filter((t) => Number(t.priceKobo) > 0).reduce((sum, t) => sum + (Number(t.quantitySold) || 0), 0);
      return `Free ${freeTickets} · Paid ${paidTickets} / ${total} · ${formatNairaFromKobo(summary?.revenueKobo || 0)}`;
    }
    const sold = summary?.paidTickets ?? types.reduce((sum, t) => sum + (Number(t.quantitySold) || 0), 0);
    const reserved = types.reduce((sum, t) => sum + (Number(t.quantityHeld) || 0), 0);
    const revenue = summary ? ` · ${formatNairaFromKobo(summary.revenueKobo)}` : "";
    return reserved > 0 ? `${sold}/${total} sold · ${reserved} reserved${revenue}` : `${sold}/${total} sold${revenue}`;
  };

  const loadOrderForTicket = async (ticket) => {
    const data = await getTicketOrder(ticket.orderId);
    return data.order;
  };

  const addTicketToWalletFromMine = async (ticket) => {
    setBusy(true);
    try {
      const [paidOrder, avail] = await Promise.all([
        loadOrderForTicket(ticket),
        getTicketPassAvailability(ticket.orderId),
      ]);
      if (avail.apple) {
        const url = await downloadAppleWalletPass(paidOrder.id);
        const a = document.createElement("a");
        a.href = url;
        a.download = `pendpay-${paidOrder.reference}.pkpass`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
      if (avail.google) {
        const data = await getGoogleWalletPassUrl(paidOrder.id);
        if (data.saveUrl) window.open(data.saveUrl, "_blank");
        return;
      }
      const blob = await buildTicketPdfBlob(paidOrder);
      downloadBlob(blob, `pendpay-tickets-${paidOrder.reference}.pdf`);
    } catch (err) {
      showToast(err.message || "Could not add to wallet");
    } finally {
      setBusy(false);
    }
  };

  const downloadTicketPdfFromMine = async (ticket) => {
    setBusy(true);
    try {
      const paidOrder = await loadOrderForTicket(ticket);
      const blob = await buildTicketPdfBlob(paidOrder);
      downloadBlob(blob, `pendpay-tickets-${paidOrder.reference}.pdf`);
    } catch (err) {
      showToast(err.message || "Could not create PDF");
    } finally {
      setBusy(false);
    }
  };

  const shareTicketFromMine = async (ticket) => {
    setBusy(true);
    try {
      const paidOrder = await loadOrderForTicket(ticket);
      await shareTicketPdf(paidOrder);
    } catch (err) {
      if (err?.name === "AbortError") return;
      showToast(err.message || "Could not share");
    } finally {
      setBusy(false);
    }
  };

  const shareEventFromUi = async (ev) => {
    if (!ev?.id) return;
    try {
      const result = await shareEventLink(ev.title, ev.id);
      if (result.aborted) return;
      if (result.ok && result.method === "clipboard") {
        showToast("Event link copied");
      } else if (!result.ok) {
        showToast("Could not share — copy: " + (result.url || ""));
      }
    } catch (err) {
      showToast(err.message || "Could not share");
    }
  };

  const resetCoverPicker = () => {
    if (coverPreview && coverPreview.startsWith("blob:")) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverFile(null);
    setCoverPreview("");
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const clearForm = () => {
    resetCoverPicker();
    setForm(emptyForm());
  };

  const startCreate = () => {
    if (!requireAuth()) return;
    setEditingId(null);
    clearForm();
    if (view === "discover" || view === "managing") {
      setReturnView(view);
    }
    setView("create");
  };

  const startEdit = (ev) => {
    if (!requireAuth()) return;
    const source = ev || event;
    if (!source) return;
    if (!ownsEvent(source)) {
      showToast("You can only edit events you organize");
      return;
    }
    if (view === "managing") setReturnView("managing");
    setEditingId(source.id);
    resetCoverPicker();
    setForm({
      title: source.title || "",
      location: source.location || "",
      startsAt: source.startsAt ? toLocalDateTimeValue(new Date(source.startsAt)) : "",
      endsAt: source.endsAt ? toLocalDateTimeValue(new Date(source.endsAt)) : "",
      endsAtEnabled: !!source.endsAt,
      tag: (source.tag || "").replace(/^#/, ""),
      description: source.description || "",
      coverImageUrl: source.coverImageUrl || "",
      tiers: (source.ticketTypes || []).map((t) => ({
        name: t.name,
        description: t.description || "",
        priceNaira: String(Math.round((t.priceKobo || 0) / 100)),
        quantityTotal: String(t.quantityTotal || 0),
      })),
    });
    setCoverPreview(source.coverImageUrl || "");
    setView("create");
  };

  const onCoverSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (coverPreview && coverPreview.startsWith("blob:")) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const clearCover = () => {
    resetCoverPicker();
    setForm((f) => ({ ...f, coverImageUrl: "" }));
  };

  const validateDates = () => {
    if (!form.startsAt) {
      showToast("Pick a start date and time");
      return false;
    }
    const starts = new Date(form.startsAt);
    if (Number.isNaN(starts.getTime())) {
      showToast("Pick a valid start date and time");
      return false;
    }
    if (starts.getTime() <= Date.now()) {
      showToast("Start date and time must be in the future");
      return false;
    }
    if (form.endsAtEnabled) {
      if (!form.endsAt) {
        showToast("Pick an end date and time, or turn off end date");
        return false;
      }
      const ends = new Date(form.endsAt);
      if (Number.isNaN(ends.getTime()) || ends.getTime() <= starts.getTime()) {
        showToast("End date and time must be after start");
        return false;
      }
    }
    return true;
  };

  const validateTiers = () => {
    const named = form.tiers.filter((t) => t.name.trim());
    if (!named.length) {
      showToast("Add at least one ticket type");
      return null;
    }
    for (const t of named) {
      const price = digitsOnly(t.priceNaira);
      const qty = digitsOnly(t.quantityTotal);
      if (price === "" || t.priceNaira !== price) {
        showToast("Price must be numbers only");
        return null;
      }
      if (qty === "" || t.quantityTotal !== qty) {
        showToast("Quantity must be numbers only");
        return null;
      }
      const quantityTotal = Number(qty);
      if (!Number.isInteger(quantityTotal) || quantityTotal < 1) {
        showToast("Quantity must be at least 1");
        return null;
      }
    }
    return named.map((t) => ({
      name: t.name.trim(),
      description: String(t.description || "").trim(),
      priceKobo: Math.round(Number(digitsOnly(t.priceNaira)) * 100),
      quantityTotal: Number(digitsOnly(t.quantityTotal)),
    }));
  };

  const submitForm = async () => {
    if (!requireAuth()) return;
    if (!validateDates()) return;
    if (editingId) {
      const target =
        (event && event.id === editingId ? event : null) ||
        myEvents.find((e) => e.id === editingId) ||
        events.find((e) => e.id === editingId);
      if (!target || !ownsEvent(target)) {
        showToast("You can only edit events you organize");
        return;
      }
    }
    const ticketTypes = editingId ? null : validateTiers();
    if (!editingId && !ticketTypes) return;
    setBusy(true);
    try {
      let coverImageUrl = form.coverImageUrl || null;
      if (coverFile) {
        coverImageUrl = await uploadEventCover(coverFile);
      }
      const payload = {
        title: form.title.trim(),
        location: form.location.trim(),
        description: form.description.trim(),
        tag: form.tag.trim(),
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: form.endsAtEnabled && form.endsAt ? new Date(form.endsAt).toISOString() : null,
        coverImageUrl: coverImageUrl || null,
      };
      if (editingId) {
        const data = await updateEvent(editingId, payload);
        setEvent(data.event);
        setEditingId(null);
        clearForm();
        setView("detail");
        showToast("Event updated", "success");
      } else {
        await createEvent({ ...payload, ticketTypes });
        clearForm();
        if (returnView === "managing") {
          await loadManaging();
        } else {
          setReturnView("discover");
          setView("discover");
          await loadDiscover();
        }
        showToast("Event published", "success");
      }
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  const requestDeleteEvent = (ev) => {
    const target = ev || event;
    if (!target || !requireAuth()) return;
    if (!ownsEvent(target)) {
      showToast("You can only delete events you organize");
      return;
    }
    setDeleteTarget(target);
  };

  const confirmDeleteEvent = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await updateEvent(deleteTarget.id, { status: "cancelled" });
      setEvent(null);
      setMyEvents((list) => list.filter((e) => e.id !== deleteTarget.id));
      setDeleteTarget(null);
      setView("managing");
      setReturnView("managing");
      try {
        const ev = await listMyEvents();
        setMyEvents(ev.events || []);
      } catch {
        /* list refresh optional */
      }
      showToast("Event deleted", "success");
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  const doCheckIn = async (rawToken) => {
    if (!requireAuth()) return;
    const token = String(rawToken || checkToken || "").trim();
    if (!token || busy || checkResult) return;
    setBusy(true);
    try {
      const data = await checkInTicket(token, checkInEventId || undefined);
      setCheckToken("");
      setCheckResult({ ok: true, ticket: data.ticket });
    } catch (err) {
      setCheckResult({ ok: false, message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const dismissCheckResult = () => {
    setCheckResult(null);
  };

  const inputStyle = {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.text,
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
  };

  const btnPrimary = {
    width: "100%",
    padding: "16px",
    background: C.lime,
    border: "none",
    borderRadius: 16,
    color: C.bg,
    fontSize: 16,
    fontWeight: 800,
    cursor: busy ? "wait" : "pointer",
    opacity: busy ? 0.7 : 1,
  };

  const btnGhost = {
    width: "100%",
    padding: "14px",
    background: "transparent",
    border: `1px solid ${C.borderBright}`,
    borderRadius: 14,
    color: C.text,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  };

  const chipStyle = (active) => ({
    padding: "8px 12px",
    borderRadius: 12,
    border: `1px solid ${active ? C.lime : C.border}`,
    background: active ? `${C.lime}18` : C.card,
    color: active ? C.lime : C.text,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  const resultsLabel = () => {
    if (query.trim()) return "Results";
    if (whenFilter === "thisWeek") return "This week";
    return "Upcoming events";
  };

  const MODE_TABS = [
    { id: "discover", label: "Discover" },
    { id: "tickets", label: "My tickets" },
    { id: "managing", label: "Managing" },
  ];

  const showModeSwitcher = view === "discover" || view === "tickets" || view === "managing";

  const selectMode = (modeId) => {
    if (modeId === "discover") {
      setReturnView("discover");
      setView("discover");
      return;
    }
    if (modeId === "tickets") {
      loadTickets();
      return;
    }
    loadManaging();
  };

  const modeSwitcher = showModeSwitcher ? (
    <div style={{
      display: "flex", gap: 6, marginBottom: 18, padding: 4,
      borderRadius: 14, border: `1px solid ${C.border}`, background: C.card,
    }}>
      {MODE_TABS.map((tab) => {
        const active = view === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => selectMode(tab.id)}
            style={{
              flex: 1,
              padding: "10px 8px",
              borderRadius: 10,
              border: active ? `1px solid ${C.lime}` : "1px solid transparent",
              background: active ? C.limeDim : "transparent",
              color: active ? C.lime : C.textMid || C.muted,
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  ) : null;

  const discoverEvents = events.filter((ev) => meDemo || ev.isDemo !== true);

  return (
    <div style={{ padding: "20px 20px 32px", minHeight: "70vh" }}>
      {guest && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <button type="button" onClick={onBack} style={{ ...btnGhost, width: "auto", padding: "8px 14px" }}>
            Back
          </button>
          <button type="button" onClick={onRequireAuth} style={{ ...btnGhost, width: "auto", padding: "8px 14px", borderColor: C.lime, color: C.lime }}>
            Sign in
          </button>
        </div>
      )}

      {modeSwitcher}

      {view === "discover" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em" }}>Discover events</h1>
            {!guest && (
              <button type="button" onClick={startCreate} style={{ ...btnGhost, width: "auto", padding: "8px 12px", fontSize: 12, borderColor: C.lime, color: C.lime }}>Create</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadDiscover()}
              placeholder="Search by name or #tag"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="button" onClick={loadDiscover} style={{
              width: 48, height: 48, borderRadius: 14, border: `1px solid ${C.border}`,
              background: C.card, color: C.lime, cursor: "pointer", fontSize: 18,
            }}>⌕</button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10, alignItems: "center" }}>
            <span style={{ color: C.muted, fontSize: 11, fontWeight: 700, marginRight: 2 }}>When</span>
            {WHEN_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setWhenFilter(f.id)}
                style={chipStyle(whenFilter === f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18, alignItems: "center" }}>
            <span style={{ color: C.muted, fontSize: 11, fontWeight: 700, marginRight: 2 }}>Sort</span>
            {SORT_OPTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSortBy(s.id)}
                style={chipStyle(sortBy === s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p style={{ color: C.muted, fontSize: 13, fontWeight: 700, margin: "0 0 12px" }}>
            {resultsLabel()}
          </p>
          {loading ? (
            <p style={{ color: C.muted }}>Loading…</p>
          ) : discoverEvents.length === 0 ? (
            <p style={{ color: C.muted }}>{guest ? "No upcoming events." : "No events yet. Create one to get started."}</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {discoverEvents.map((ev) => (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => {
                    markOpenedFromDiscovery();
                    openEvent(ev.id, "discover");
                  }}
                  style={{
                    display: "flex", gap: 14, alignItems: "center", textAlign: "left",
                    padding: 14, borderRadius: 16, border: `1px solid ${C.border}`,
                    background: C.card, cursor: "pointer", color: C.text,
                  }}
                >
                  <div style={{
                    width: 56, height: 56, borderRadius: 12, background: C.forestBright,
                    flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    color: C.muted, fontSize: 22, overflow: "hidden",
                  }}>
                    {ev.coverImageUrl ? (
                      <img
                        src={ev.coverImageUrl}
                        alt=""
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      "▦"
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>
                      {ev.title}
                      {ev.pricing === "free" ? <span style={{ marginLeft: 8, color: C.lime, fontSize: 11, fontWeight: 800 }}>FREE</span> : null}
                      {ev.pricing === "mixed" ? <span style={{ marginLeft: 8, color: C.lime, fontSize: 11, fontWeight: 800 }}>FREE & PAID</span> : null}
                      {ev.isDemo ? <span style={{ marginLeft: 8, color: C.lime, fontSize: 11, fontWeight: 800 }}>SAMPLE</span> : null}
                    </div>
                    <div style={{ color: C.muted, fontSize: 12 }}>{formatEventWhenShort(ev.startsAt, ev.location, ev.endsAt)}</div>
                    {ev.tag && <div style={{ color: C.lime, fontSize: 12, marginTop: 4, fontWeight: 600 }}>{ev.tag}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {view === "detail" && event && (
        <>
          <button type="button" onClick={() => setView(returnView || "discover")} style={{ ...btnGhost, width: "auto", padding: "8px 12px", marginBottom: 16 }}>← Back</button>
          {event.coverImageUrl ? (
            <div style={{
              width: "100%",
              aspectRatio: "16 / 9",
              borderRadius: 16,
              overflow: "hidden",
              marginBottom: 16,
              background: C.forestBright,
            }}>
              <img
                src={event.coverImageUrl}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </div>
          ) : null}
          <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 900 }}>{event.title}</h2>
          <p style={{ margin: "0 0 6px", color: C.muted, fontSize: 13 }}>
            {formatEventWhenDetail(event.startsAt, event.endsAt)}
            {event.location ? ` · ${event.location}` : ""}
          </p>
          {event.description && (
            <p style={{ margin: "0 0 16px", color: C.textMid || C.muted, fontSize: 14, lineHeight: 1.5 }}>{event.description}</p>
          )}
          <button
            type="button"
            onClick={() => shareEventFromUi(event)}
            style={{ ...btnGhost, width: "auto", padding: "8px 12px", marginBottom: 16 }}
          >
            Share event
          </button>
          {isOrganizer && event.status !== "cancelled" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => startEdit(event)}
                style={{ ...btnGhost, flex: 1, minWidth: 100, padding: "10px 12px", fontSize: 13 }}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => openCheckIn(event)}
                style={{ ...btnGhost, flex: 1, minWidth: 100, padding: "10px 12px", fontSize: 13 }}
              >
                Check in
              </button>
              <button
                type="button"
                onClick={() => setView("analytics")}
                style={{ ...btnGhost, flex: 1, minWidth: 100, padding: "10px 12px", fontSize: 13, borderColor: C.lime, color: C.lime }}
              >
                Analytics
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => requestDeleteEvent(event)}
                style={{
                  ...btnGhost, flex: 1, minWidth: 100, padding: "10px 12px", fontSize: 13,
                  borderColor: `${C.danger}55`, color: C.danger,
                }}
              >
                Delete
              </button>
            </div>
          )}
          {isOrganizer ? (
            <>
              {event.organizerSummary ? (
                <div style={{
                  padding: 16, borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, marginBottom: 14,
                }}>
                  {event.organizerSummary.pricing === "free" ? (
                    <>
                      <div style={{ fontWeight: 800 }}>Registrations: {event.organizerSummary.freeTickets} / {event.organizerSummary.capacity}</div>
                      <div style={{ color: C.muted, marginTop: 6 }} title="Ticket face value. PendPay service fees are not included.">Ticket revenue: {formatNairaFromKobo(event.organizerSummary.revenueKobo)}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 800 }}>
                        {event.organizerSummary.pricing === "mixed"
                          ? `Free ${event.organizerSummary.freeTickets} · Paid ${event.organizerSummary.paidTickets} / ${event.organizerSummary.capacity}`
                          : `${event.organizerSummary.paidTickets} / ${event.organizerSummary.capacity} sold`}
                      </div>
                      <div style={{ color: C.muted, marginTop: 6 }} title="Ticket face value. PendPay service fees are not included.">Ticket revenue: {formatNairaFromKobo(event.organizerSummary.revenueKobo)}</div>
                      <div style={{ color: C.muted, marginTop: 6 }} title="Waiting for the holding period. This is not withdrawable.">Pending settlement: {formatNairaFromKobo(event.organizerSummary.pendingSettlementKobo || 0)}</div>
                      <div style={{ color: C.muted, marginTop: 6 }} title="Released to the organiser wallet.">Available: {formatNairaFromKobo(event.organizerSummary.availableSettlementKobo || 0)}</div>
                      {event.organizerSummary.nextReleaseAt ? (
                        <div style={{ color: C.muted, marginTop: 6 }}>Next release: {new Date(event.organizerSummary.nextReleaseAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}</div>
                      ) : null}
                      {(event.organizerSummary.withheldKobo || 0) > 0 ? (
                        <div style={{ color: C.muted, marginTop: 6 }} title="Held for a refund, cancellation, or another block.">Withheld: {formatNairaFromKobo(event.organizerSummary.withheldKobo)}</div>
                      ) : null}
                    </>
                  )}
                  <div style={{ color: C.muted, marginTop: 6 }}>Checked in: {event.organizerSummary.checkedIn}</div>
                </div>
              ) : null}
              <p style={{ color: C.muted, fontSize: 13, fontWeight: 700, margin: "0 0 10px" }}>Ticket inventory</p>
              {(event.ticketTypes || []).map((t) => {
                const sold = Number(t.quantitySold) || 0;
                const total = Number(t.quantityTotal) || 0;
                const reserved = Math.max(0, Number(t.quantityHeld) || 0);
                const available = Math.max(0, Number(t.quantityRemaining) || 0);
                const freeCategory = Number(t.priceKobo) === 0;
                const stats = [
                  ["Capacity", total],
                  [freeCategory ? "Registered" : "Sold", sold],
                  ["Reserved", reserved],
                  ["Available", available],
                ];
                return (
                  <div key={t.id} style={{
                    padding: 16, borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, marginBottom: 12,
                  }}>
                    <div style={{ fontWeight: 800 }}>{t.name}</div>
                    <div style={{ color: C.lime, fontWeight: 700, marginTop: 4 }}>{priceLabel(t.priceKobo)}</div>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                      gap: 8,
                      marginTop: 12,
                    }}>
                      {stats.map(([label, value]) => (
                        <div key={label} style={{ minWidth: 0 }}>
                          <div style={{ color: C.muted, fontSize: 11, fontWeight: 700 }}>{label}</div>
                          <div style={{ fontWeight: 800, fontSize: 16, marginTop: 2 }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          ) : event.isDemo && !meDemo ? (
            <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>This event is not available.</p>
          ) : event.isDemo ? (
            <>
              <div style={{
                marginBottom: 14, padding: "12px 14px", borderRadius: 12,
                background: C.lime + "14", border: `1px solid ${C.lime}55`,
              }}>
                <p style={{ color: C.lime, fontSize: 12, fontWeight: 800, margin: "0 0 4px" }}>Sample event</p>
                <p style={{ color: C.muted, fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                  Ticket types are here for the walkthrough. Purchases are turned off.
                </p>
              </div>
              {(event.ticketTypes || []).map((t) => (
                <div key={t.id} style={{
                  padding: 16, borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, marginBottom: 12,
                }}>
                  <div style={{ fontWeight: 800 }}>{t.name}</div>
                  <div style={{ color: C.lime, fontWeight: 700, marginTop: 4 }}>{priceLabel(t.priceKobo)}</div>
                </div>
              ))}
            </>
          ) : (
            <>
              {(event.ticketTypes || []).map((t) => {
                const remaining = Math.max(0, Number(t.quantityRemaining) || 0);
                const selected = qty[t.id] || 0;
                const canDecrease = selected > 0;
                const canIncrease = selected < remaining;
                return (
                <div key={t.id} style={{
                  padding: 16, borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, marginBottom: 12,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 800 }}>{t.name}{Number(t.priceKobo) === 0 ? <span style={{ marginLeft: 8, color: C.lime, fontSize: 11 }}>FREE</span> : null}</div>
                      <div style={{ color: C.lime, fontWeight: 700, marginTop: 4 }}>{priceLabel(t.priceKobo)}</div>
                      {t.description ? <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>{t.description}</div> : null}
                      <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>
                        {remaining > 0 ? `${remaining} left` : "Sold out"}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <button
                        type="button"
                        disabled={!canDecrease}
                        onClick={() => {
                          if (!canDecrease) return;
                          setQty((q) => ({ ...q, [t.id]: Math.max(0, (q[t.id] || 0) - 1) }));
                        }}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          border: `1px solid ${C.border}`,
                          background: C.surface,
                          color: canDecrease ? C.text : C.muted,
                          cursor: canDecrease ? "pointer" : "not-allowed",
                          opacity: canDecrease ? 1 : 0.35,
                        }}
                      >−</button>
                      <span style={{ fontWeight: 800, minWidth: 20, textAlign: "center" }}>{selected}</span>
                      <button
                        type="button"
                        disabled={!canIncrease}
                        onClick={() => {
                          if (!canIncrease) return;
                          setQty((q) => ({ ...q, [t.id]: Math.min(remaining, (q[t.id] || 0) + 1) }));
                        }}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          border: canIncrease ? "none" : `1px solid ${C.border}`,
                          background: canIncrease ? C.lime : C.surface,
                          color: canIncrease ? C.bg : C.muted,
                          cursor: canIncrease ? "pointer" : "not-allowed",
                          fontWeight: 800,
                          opacity: canIncrease ? 1 : 0.45,
                        }}
                      >+</button>
                    </div>
                  </div>
                </div>
                );
              })}
              {selectionKindOf(event, qty) === "free" ? (
                <div style={{ display: "flex", justifyContent: "space-between", margin: "18px 0 12px", fontWeight: 800 }}>
                  <span>Total</span>
                  <span style={{ color: C.lime }}>FREE</span>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", margin: "18px 0 6px", fontWeight: 700 }}>
                    <span style={{ color: C.muted }}>Tickets</span>
                    <span>{formatNairaFromKobo(subtotalKobo())}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", margin: "0 0 6px", fontWeight: 700 }}>
                    <span style={{ color: C.muted }} title="Charged on each paid ticket. Free tickets are ₦0.">PendPay service fee</span>
                    <span>{formatNairaFromKobo(checkoutFeeKobo())}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", margin: "0 0 6px", fontWeight: 700 }}>
                    <span style={{ color: C.muted }} title="Shown for the payment method you choose. ₦0 until that provider's rate is configured.">Payment processing fee</span>
                    <span>{processingIsConfigured(config?.fees) ? "At payment" : formatNairaFromKobo(0)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", margin: "0 0 12px", fontWeight: 800 }}>
                    <span>Total</span>
                    <span style={{ color: C.lime }}>{formatNairaFromKobo(checkoutTotalKobo())}</span>
                  </div>
                  {selectionKindOf(event, qty) === "mixed" ? (
                    <p style={{ color: C.muted, fontSize: 12, margin: "0 0 12px" }}>Free tickets are included after payment.</p>
                  ) : null}
                </>
              )}
              <button
                type="button"
                disabled={busy || !selectedItems().length || (selectionKindOf(event, qty) !== "free" && subtotalKobo() <= 0)}
                onClick={goPay}
                style={btnPrimary}
              >
                {selectionKindOf(event, qty) === "free"
                  ? "Register for Free"
                  : (guest || !getToken())
                    ? `Checkout · ${formatNairaFromKobo(checkoutTotalKobo())}`
                    : `Agree and pay · ${formatNairaFromKobo(checkoutTotalKobo())}`}
              </button>
            </>
          )}
        </>
      )}

      {view === "guestCheckout" && event && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitGuestCheckout();
          }}
        >
          <button type="button" onClick={() => setView("detail")} style={{ ...btnGhost, width: "auto", padding: "8px 12px", marginBottom: 16 }}>← Back</button>
          <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 900 }}>
            {selectionKindOf(event, qty) === "free" ? "Register for free" : "Continue as guest"}
          </h2>
          <p style={{ color: C.muted, marginTop: 0 }}>Name and email are enough. No account, PIN, tag, or ID number.</p>
          <label style={{ display: "block", margin: "12px 0 6px", color: C.muted, fontSize: 13 }}>Full name</label>
          <input value={guestForm.name} onChange={(e) => setGuestForm((f) => ({ ...f, name: e.target.value }))} autoComplete="name" style={inputStyle} />
          <label style={{ display: "block", margin: "12px 0 6px", color: C.muted, fontSize: 13 }}>Email</label>
          <input value={guestForm.email} onChange={(e) => setGuestForm((f) => ({ ...f, email: e.target.value }))} type="email" autoComplete="email" style={inputStyle} />
          <label style={{ display: "block", margin: "12px 0 6px", color: C.muted, fontSize: 13 }}>Phone (optional)</label>
          <input value={guestForm.phone} onChange={(e) => setGuestForm((f) => ({ ...f, phone: e.target.value }))} type="tel" autoComplete="tel" placeholder="08012345678" style={inputStyle} />
          <div style={{ display: "flex", justifyContent: "space-between", margin: "18px 0 12px", fontWeight: 800 }}>
            <span>Total</span>
            <span style={{ color: C.lime }}>
              {selectionKindOf(event, qty) === "free"
                ? "FREE"
                : formatNairaFromKobo(checkoutTotalKobo())}
            </span>
          </div>
          <button type="submit" disabled={busy} style={btnPrimary}>
            {busy
              ? "Please wait…"
              : selectionKindOf(event, qty) === "free"
                ? "Register for Free"
                : "Continue as guest"}
          </button>
          {!(getToken() && !guest) ? (
            <button type="button" onClick={stashSelectionAndSignIn} style={{ ...btnGhost, marginTop: 10 }}>
              Sign in to PendPay
            </button>
          ) : null}
        </form>
      )}

      {view === "pay" && order && (
        <>
          <button type="button" onClick={leavePayView} style={{ ...btnGhost, width: "auto", padding: "8px 12px", marginBottom: 16 }}>← Back</button>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 6px" }}>Amount due</p>
          <h2 style={{ margin: "0 0 8px", fontSize: 36, fontWeight: 900 }}>{formatNairaFromKobo(chargeQuote?.chargeKobo ?? order.totalKobo)}</h2>
          <div style={{ display: "grid", gap: 4, marginBottom: 18, fontSize: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: C.muted }}>Ticket</span><span>{formatNairaFromKobo(chargeQuote?.amountKobo ?? order.amountKobo)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: C.muted }} title="PendPay service fee for this order.">PendPay service fee</span><span>{formatNairaFromKobo(chargeQuote?.feeKobo ?? order.feeKobo)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: C.muted }} title={chargeQuote?.processingNote || "Payment processing fee for the method you selected."}>Payment processing fee</span><span>{formatNairaFromKobo(chargeQuote?.processingFeeKobo ?? order.processingFeeKobo ?? 0)}</span></div>
          </div>
          <p style={{ color: C.muted, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Choose how to pay</p>

          {[
            { id: "wallet", label: "PendPay wallet" },
            { id: "bank_transfer", label: "Bank transfer" },
            { id: "card", label: "Debit or credit card" },
          ].filter((m) => !(order.isGuest && m.id === "wallet")).map((m) => (
            <div
              key={m.id}
              style={{
                width: "100%", textAlign: "left", padding: 16, marginBottom: 10, borderRadius: 16,
                border: `1.5px solid ${payMethod === m.id ? C.lime : C.border}`,
                background: C.card, color: C.text,
              }}
            >
              <button
                type="button"
                onClick={() => selectPayMethod(m.id)}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  width: "100%", fontWeight: 800, background: "transparent", border: "none",
                  color: C.text, cursor: "pointer", padding: 0, font: "inherit",
                }}
              >
                <span>{m.label}</span>
                {payMethod === m.id && <span style={{ color: C.lime }}>✓</span>}
              </button>
              {m.id === "bank_transfer" && payMethod === "bank_transfer" && funding && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: C.surface, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>
                    {funding.accountName || "PendPay"} · transfer exact amount
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: "0.06em", flex: 1 }}>
                      {String(funding.accountNumber).replace(/(\d{4})(?=\d)/g, "$1 ")}
                    </div>
                    <button
                      type="button"
                      aria-label="Copy account number"
                      title="Copy account number"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(String(funding.accountNumber));
                        } catch {
                          /* ignore */
                        }
                      }}
                      style={{
                        background: "transparent", border: "none", color: C.text, cursor: "pointer",
                        padding: 4, display: "flex", alignItems: "center",
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
                    {funding.bankName || "Safe Haven MFB"}
                    {funding.amountKobo != null ? ` · ${formatNairaFromKobo(funding.amountKobo)}` : ""}
                  </div>
                  {funding.expiresAt && (
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                      {formatValidForCountdown(funding.expiresAt, fundingNowMs)}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: C.lime, marginTop: 8 }}>Watching for payment automatically…</div>
                </div>
              )}
            </div>
          ))}

          {!(payMethod === "bank_transfer" && funding) && (
            <button type="button" disabled={busy} onClick={payNow} style={{ ...btnPrimary, marginTop: 8 }}>
              {payMethod === "bank_transfer" ? "Show account details" : `Pay · ${formatNairaFromKobo(chargeQuote?.chargeKobo ?? order.totalKobo)}`}
            </button>
          )}
        </>
      )}

      {view === "refund" && order && (
        <div>
          <h2 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 900 }}>Tickets were not issued</h2>
          <p style={{ color: C.muted, marginBottom: 20 }}>
            Payment for {order.reference} was received, but the seats were no longer available.
            {order.status === "refunded"
              ? " The payment has been refunded."
              : order.status === "refund_pending"
                ? " A refund has been sent and is waiting for confirmation."
                : order.status === "refund_failed"
                  ? " The refund did not complete. It stays open until it is confirmed."
                  : " This payment is recorded for a refund."}
          </p>
          <button type="button" onClick={() => setView("detail")} style={btnPrimary}>Back to event</button>
        </div>
      )}

      {view === "success" && order && (
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%", border: `2px solid ${C.lime}`,
            display: "flex", alignItems: "center", justifyContent: "center", margin: "12px auto 16px",
            color: C.lime, fontSize: 28, fontWeight: 900,
          }}>✓</div>
          <h2 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 900 }}>
            {order.status === "registered" || order.isFreeRegistration ? "You're registered" : "You're in"}
          </h2>
          <p style={{ color: C.muted, marginBottom: 20 }}>
            {(order.lineItems || []).map((li) => `${li.quantity}× ${li.name}`).join(", ")}
            {order.status === "registered" || order.isFreeRegistration ? " Your ticket is ready." : ""}
          </p>
          {(order.tickets || []).map((t) => (
            <div key={t.id} style={{
              display: "inline-block", background: "#fff", padding: 16, borderRadius: 12, marginBottom: 12,
            }}>
              <QrImage value={t.qrToken} size={180} />
              <div style={{ color: "#0B1120", fontSize: 12, marginTop: 8, fontWeight: 700 }}>{t.ticketTypeName}</div>
            </div>
          ))}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
            <button
              type="button"
              style={btnGhost}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  if (passAvail.apple) {
                    const url = await downloadAppleWalletPass(order.id);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `pendpay-${order.reference}.pkpass`;
                    a.click();
                    URL.revokeObjectURL(url);
                    return;
                  }
                  if (passAvail.google) {
                    const data = await getGoogleWalletPassUrl(order.id);
                    if (data.saveUrl) window.open(data.saveUrl, "_blank");
                    return;
                  }
                  const blob = await buildTicketPdfBlob(order);
                  downloadBlob(blob, `pendpay-tickets-${order.reference}.pdf`);
                } catch (err) {
                  showToast(err.message || "Could not add to wallet");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Add to wallet
            </button>
            <button
              type="button"
              style={btnGhost}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const blob = await buildTicketPdfBlob(order);
                  downloadBlob(blob, `pendpay-tickets-${order.reference}.pdf`);
                } catch (err) {
                  showToast(err.message || "Could not create PDF");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Download PDF
            </button>
            <button
              type="button"
              style={btnGhost}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await shareTicketPdf(order);
                } catch (err) {
                  if (err?.name === "AbortError") return;
                  showToast(err.message || "Could not share");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Share PDF
            </button>
            {event?.id || order?.eventId ? (
              <button
                type="button"
                style={btnGhost}
                onClick={() =>
                  shareEventFromUi({
                    id: event?.id || order.eventId,
                    title: event?.title || order.tickets?.[0]?.eventTitle,
                  })
                }
              >
                Share event
              </button>
            ) : null}
            <button type="button" style={btnPrimary} onClick={() => {
              if (order?.isGuest) {
                setView("discover");
                return;
              }
              loadTickets();
            }}>Done</button>
          </div>
          {order?.isGuest && showAccountInvite ? (
            <div style={{ textAlign: "left", marginTop: 20, padding: 16, borderRadius: 16, border: `1px solid ${C.border}`, background: C.card }}>
              <h3 style={{ margin: "0 0 8px" }}>Enjoyed using PendPay?</h3>
              <p style={{ color: C.muted, marginTop: 0 }}>
                Create a PendPay account to manage your tickets, access your wallet and enjoy faster payments next time.
              </p>
              <button type="button" style={btnPrimary} onClick={() => onCreateAccount?.()}>
                Create PendPay Account
              </button>
              <button type="button" style={{ ...btnGhost, marginTop: 8 }} onClick={() => setShowAccountInvite(false)}>
                Not Now
              </button>
            </div>
          ) : null}
        </div>
      )}

      {view === "create" && (
        <>
          <button
            type="button"
            onClick={() => {
              const wasEditing = !!editingId;
              setEditingId(null);
              clearForm();
              setView(wasEditing && event ? "detail" : (returnView || "discover"));
            }}
            style={{ ...btnGhost, width: "auto", padding: "8px 12px", marginBottom: 16 }}
          >
            ← Back
          </button>
          <h2 style={{ margin: "0 0 16px", fontSize: 22, fontWeight: 900 }}>
            {editingId ? "Edit event" : "Create event"}
          </h2>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 12, color: C.muted, marginBottom: 6 }}>
              Cover image (optional)
            </label>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={onCoverSelected}
              style={{ display: "none" }}
            />
            {(coverPreview || form.coverImageUrl) ? (
              <div style={{
                position: "relative",
                width: "100%",
                aspectRatio: "16 / 9",
                borderRadius: 14,
                overflow: "hidden",
                background: C.forestBright,
                marginBottom: 8,
              }}>
                <img
                  src={coverPreview || form.coverImageUrl}
                  alt="Cover preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                style={{
                  ...btnGhost,
                  marginBottom: 8,
                  borderStyle: "dashed",
                  color: C.muted,
                }}
              >
                Add cover image
              </button>
            )}
            {(coverPreview || form.coverImageUrl) ? (
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  style={{ ...btnGhost, flex: 1, padding: "10px 12px", fontSize: 13 }}
                >
                  Change
                </button>
                <button
                  type="button"
                  onClick={clearCover}
                  style={{ ...btnGhost, flex: 1, padding: "10px 12px", fontSize: 13, color: C.danger }}
                >
                  Remove
                </button>
              </div>
            ) : null}
          </div>
          {[
            ["title", "Event title", "text"],
            ["location", "Location", "text"],
            ["tag", "Tag (e.g. fishfair.lagos)", "text"],
          ].map(([key, label, type]) => (
            <div key={key} style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, color: C.muted, marginBottom: 6 }}>{label}</label>
              <input
                type={type}
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                style={inputStyle}
              />
            </div>
          ))}
          <EventDateTimePicker
            label="Starts at"
            value={form.startsAt}
            onChange={(v) => setForm((f) => ({ ...f, startsAt: v }))}
            minDateTime={new Date()}
          />
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              margin: "4px 0 12px",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 700,
              color: C.text,
            }}
          >
            <input
              type="checkbox"
              checked={form.endsAtEnabled}
              onChange={(e) => {
                const on = e.target.checked;
                setForm((f) => ({
                  ...f,
                  endsAtEnabled: on,
                  endsAt: on ? f.endsAt : "",
                }));
              }}
              style={{ width: 18, height: 18, accentColor: C.lime }}
            />
            Set end date &amp; time
          </label>
          {form.endsAtEnabled && (
            <EventDateTimePicker
              label="Ends at"
              value={form.endsAt}
              onChange={(v) => setForm((f) => ({ ...f, endsAt: v }))}
              minDateTime={form.startsAt || new Date()}
            />
          )}
          <label style={{ display: "block", fontSize: 12, color: C.muted, marginBottom: 6 }}>Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={3}
            style={{ ...inputStyle, resize: "vertical", marginBottom: 14 }}
          />
          {editingId ? (
            <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
              Ticket types can&apos;t be changed after publish.
            </p>
          ) : (
            <>
              <p style={{ fontWeight: 800, marginBottom: 4 }}>Ticket types</p>
              <p style={{ color: C.muted, fontSize: 13, marginTop: 0 }}>Enter 0 to make a ticket free. Free and paid categories can be on the same event.</p>
              {form.tiers.map((tier, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 1fr 0.8fr auto",
                    gap: 8,
                    marginBottom: 8,
                    alignItems: "center",
                  }}
                >
                  <input
                    placeholder="Name"
                    value={tier.name}
                    onChange={(e) => {
                      const tiers = [...form.tiers];
                      tiers[idx] = { ...tiers[idx], name: e.target.value };
                      setForm((f) => ({ ...f, tiers }));
                    }}
                    style={inputStyle}
                  />
                  <input
                    placeholder="Price ₦"
                    aria-label={`Price for ${tier.name || "ticket"}`}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={tier.priceNaira}
                    onChange={(e) => {
                      const tiers = [...form.tiers];
                      tiers[idx] = { ...tiers[idx], priceNaira: digitsOnly(e.target.value) };
                      setForm((f) => ({ ...f, tiers }));
                    }}
                    style={inputStyle}
                  />
                  <input
                    placeholder="Qty"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={tier.quantityTotal}
                    onChange={(e) => {
                      const tiers = [...form.tiers];
                      tiers[idx] = { ...tiers[idx], quantityTotal: digitsOnly(e.target.value) };
                      setForm((f) => ({ ...f, tiers }));
                    }}
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${tier.name || "ticket type"}`}
                    disabled={form.tiers.length <= 1}
                    onClick={() => {
                      if (form.tiers.length <= 1) return;
                      setForm((f) => ({ ...f, tiers: f.tiers.filter((_, i) => i !== idx) }));
                    }}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      border: `1px solid ${C.border}`,
                      background: form.tiers.length <= 1 ? C.card : C.dangerDim,
                      color: form.tiers.length <= 1 ? C.muted : C.danger,
                      cursor: form.tiers.length <= 1 ? "not-allowed" : "pointer",
                      fontSize: 18,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                  <input
                    placeholder="Description (optional)"
                    value={tier.description || ""}
                    onChange={(e) => {
                      const tiers = [...form.tiers];
                      tiers[idx] = { ...tiers[idx], description: e.target.value.slice(0, 500) };
                      setForm((f) => ({ ...f, tiers }));
                    }}
                    style={{ ...inputStyle, gridColumn: "1 / -1" }}
                  />
                  {tier.priceNaira === "0" ? (
                    <div style={{ gridColumn: "1 / -1", color: C.lime, fontSize: 12, fontWeight: 800 }}>FREE</div>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, tiers: [...f.tiers, { ...EMPTY_TIER }] }))}
                style={{ ...btnGhost, marginBottom: 16 }}
              >
                Add ticket type
              </button>
            </>
          )}
          <button type="button" disabled={busy} onClick={submitForm} style={btnPrimary}>
            {editingId ? "Save changes" : "Publish event"}
          </button>
        </>
      )}

      {view === "tickets" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em" }}>My tickets</h1>
          </div>
          {myTickets.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 12px" }}>
              <p style={{ color: C.muted, marginBottom: 16 }}>No tickets yet.</p>
              <button
                type="button"
                onClick={() => { setReturnView("discover"); setView("discover"); }}
                style={{ ...btnPrimary, width: "auto", padding: "12px 20px", display: "inline-block" }}
              >
                Discover events
              </button>
            </div>
          ) : (
            myTickets.map((t) => (
              <div key={t.id} style={{
                padding: 14, borderRadius: 14, border: `1px solid ${C.border}`, background: C.card, marginBottom: 10,
              }}>
                <div style={{ fontWeight: 800 }}>{t.eventTitle}</div>
                <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
                  {t.ticketTypeName} · {t.status} · {formatEventWhen(t.eventStartsAt)}
                </div>
                {t.status === "valid" && (
                  <>
                    <div style={{ marginTop: 10, display: "flex", justifyContent: "center" }}>
                      <QrImage value={t.qrToken} size={140} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                      <button
                        type="button"
                        style={{ ...btnGhost, padding: "10px 12px", fontSize: 13 }}
                        disabled={busy}
                        onClick={() => addTicketToWalletFromMine(t)}
                      >
                        Add to wallet
                      </button>
                      <button
                        type="button"
                        style={{ ...btnGhost, padding: "10px 12px", fontSize: 13 }}
                        disabled={busy}
                        onClick={() => downloadTicketPdfFromMine(t)}
                      >
                        Download PDF
                      </button>
                      <button
                        type="button"
                        style={{ ...btnGhost, padding: "10px 12px", fontSize: 13 }}
                        disabled={busy}
                        onClick={() => shareTicketFromMine(t)}
                      >
                        Share PDF
                      </button>
                      {t.eventId ? (
                        <button
                          type="button"
                          style={{ ...btnGhost, padding: "10px 12px", fontSize: 13 }}
                          onClick={() =>
                            shareEventFromUi({ id: t.eventId, title: t.eventTitle })
                          }
                        >
                          Share event
                        </button>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </>
      )}

      {view === "analytics" && event?.id && (
        <EventAnalyticsPanel
          eventId={event.id}
          onBack={() => {
            const next = returnView || "managing";
            if (event?.id) void refreshOpenEvent(event.id);
            if (next === "managing") {
              listMyEvents().then((ev) => setMyEvents(ev.events || [])).catch(() => {});
            }
            setView(next);
          }}
        />
      )}

      {view === "managing" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em" }}>Managing</h1>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={() => openCheckIn()} style={{ ...btnGhost, width: "auto", padding: "8px 12px", fontSize: 12 }}>Check in</button>
              <button type="button" onClick={startCreate} style={{ ...btnGhost, width: "auto", padding: "8px 12px", fontSize: 12, borderColor: C.lime, color: C.lime }}>Create</button>
            </div>
          </div>
          {myEvents.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 12px" }}>
              <p style={{ color: C.muted, marginBottom: 16 }}>You&apos;re not organizing any events.</p>
              <button
                type="button"
                onClick={startCreate}
                style={{ ...btnPrimary, width: "auto", padding: "12px 20px", display: "inline-block" }}
              >
                Create event
              </button>
            </div>
          ) : (
            myEvents.map((ev) => {
              const sold = soldSummary(ev);
              return (
                <div
                  key={ev.id}
                  style={{
                    padding: 14, marginBottom: 10, borderRadius: 14,
                    border: `1px solid ${C.border}`, background: C.card,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openEvent(ev.id, "managing")}
                    style={{
                      display: "block", width: "100%", textAlign: "left", padding: 0,
                      border: "none", background: "transparent", color: C.text, cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{ev.title}</div>
                    <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
                      {formatEventWhenShort(ev.startsAt, ev.location, ev.endsAt)}
                      {ev.status && ev.status !== "published" ? ` · ${ev.status}` : ""}
                    </div>
                    {sold ? (
                      <div style={{ color: C.lime, fontSize: 12, fontWeight: 700, marginTop: 6 }}>{sold}</div>
                    ) : null}
                  </button>
                  {ev.status !== "cancelled" && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={() => openEvent(ev.id, "managing")}
                        style={{ ...btnGhost, flex: 1, minWidth: 70, padding: "8px 10px", fontSize: 12 }}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(ev)}
                        style={{ ...btnGhost, flex: 1, minWidth: 70, padding: "8px 10px", fontSize: 12 }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => openCheckIn(ev)}
                        style={{ ...btnGhost, flex: 1, minWidth: 70, padding: "8px 10px", fontSize: 12 }}
                      >
                        Check in
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEvent(ev);
                          setReturnView("managing");
                          setView("analytics");
                        }}
                        style={{ ...btnGhost, flex: 1, minWidth: 70, padding: "8px 10px", fontSize: 12, borderColor: C.lime, color: C.lime }}
                      >
                        Analytics
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => requestDeleteEvent(ev)}
                        style={{
                          ...btnGhost, flex: 1, minWidth: 70, padding: "8px 10px", fontSize: 12,
                          borderColor: `${C.danger}55`, color: C.danger,
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </>
      )}

      {view === "checkin" && (
        <>
          <button type="button" onClick={() => setView("managing")} style={{ ...btnGhost, width: "auto", padding: "8px 12px", marginBottom: 16 }}>← Back</button>
          <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 900 }}>Check in</h2>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
            {checkInEventTitle
              ? `Scanning for “${checkInEventTitle}”.`
              : "Scan the attendee\u2019s ticket QR code with your camera."}
          </p>
          {!checkResult && (
            <>
              <TicketQrScanner paused={busy} onScan={(token) => doCheckIn(token)} />
              <button
                type="button"
                onClick={() => setShowManualCheckIn((v) => !v)}
                style={{ ...btnGhost, marginBottom: showManualCheckIn ? 12 : 0 }}
              >
                {showManualCheckIn ? "Hide manual entry" : "Enter code manually"}
              </button>
              {showManualCheckIn && (
                <>
                  <input
                    value={checkToken}
                    onChange={(e) => setCheckToken(e.target.value)}
                    placeholder="QR token"
                    style={{ ...inputStyle, marginBottom: 12 }}
                  />
                  <button type="button" disabled={busy || !checkToken.trim()} onClick={() => doCheckIn()} style={btnPrimary}>
                    Check in ticket
                  </button>
                </>
              )}
            </>
          )}
          {checkResult && (
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="checkin-result-title"
              style={{
                position: "fixed",
                inset: 0,
                maxWidth: 430,
                margin: "0 auto",
                left: 0,
                right: 0,
                background: "rgba(0,0,0,0.85)",
                zIndex: 4000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
              onClick={dismissCheckResult}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 380,
                  background: C.surface,
                  borderRadius: 24,
                  padding: "28px 24px",
                  border: `1px solid ${C.border}`,
                  textAlign: "center",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: (checkResult.ok ? C.lime : C.danger) + "18",
                    border: `2px solid ${(checkResult.ok ? C.lime : C.danger)}30`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                    color: checkResult.ok ? C.lime : C.danger,
                    fontSize: 22,
                    fontWeight: 900,
                  }}
                >
                  {checkResult.ok ? "✓" : "!"}
                </div>
                <p
                  id="checkin-result-title"
                  style={{ color: C.text, fontSize: 17, fontWeight: 900, margin: "0 0 8px" }}
                >
                  {checkResult.ok ? "Checked in" : "Check-in failed"}
                </p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.5, margin: "0 0 24px" }}>
                  {checkResult.ok
                    ? `${checkResult.ticket.ticketTypeName} · ${checkResult.ticket.eventTitle}`
                    : checkResult.message}
                </p>
                <button type="button" onClick={dismissCheckResult} style={btnPrimary}>
                  {checkResult.ok ? "Scan next" : "Try again"}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete event?"
        message={
          deleteTarget
            ? `Delete “${deleteTarget.title}”? It will be cancelled and removed from Discover.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        busy={busy}
        onCancel={() => { if (!busy) setDeleteTarget(null); }}
        onConfirm={confirmDeleteEvent}
      />
    </div>
  );
}
